import React, { useState, useEffect, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Form, Icon, Popup } from 'semantic-ui-react';
import formatCurrency from 'format-currency';
import { Products, Rates, Packages, RateAssignment, RoomTypes } from '../../../api/services/next';
import GridSearchTemplate from "../../GridSearchTemplate";
import EntityTypes from "../../GroupReservation/Constants/EntityTypes";
import { EntityType } from '../../../components/shared/constants';
import { dateFormat } from '../../../components/shared/constants';
import { groupByIntoArray } from '../../../components/shared/utils'
import { useTextResources, useMessageResources } from '../../../language';
import { currencyFormat } from '../../../components/shared/utils';
import { onError, onWarning } from '../../../components/shared/messages';
import RoomTypesDropDown from '../../../components/shared/RoomTypesDropDown';
import DropDown from '../../../components/shared/dropdown';
import { PaxInput } from '../../../components/PaxInput';
import { DatePicker } from '../../../components/shared/DatePicker';
import { PriceListContext } from "../context";
import { useLoader } from '../../../hooks/useLoader';
import CheckboxWrapper from '../../../components/shared/Checkbox';
import RestrictionsPopup from '../../../components/RestrictionsPopup';

export const FinalPrice = ({ HotelId, RateId }) => {
    const text = useTextResources();
    const message = useMessageResources();

    const [datesRange, setDatesRange] = useState();
    const [data, setData] = useState([]);
    const [packages, setPackages] = useState([]);
    const [roomTypes, setRoomTypes] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [selectedPackageId, setSelectedPackageId] = useState();
    const [showRestrictions, setShowRestrictions] = useState(false);
    const roomOnlyPackageKey = 'RoomOnly';

    const { state } = useContext(PriceListContext);
    const loader = useLoader();
    const doSearch = useRef();

    useEffect(() => {
        (async () => {
            try {
                loader(true);

                const [packages, roomTypes, ratePricingElements, rateAssignments] = await Promise.all([
                    Packages.GetPackages({
                        IncludePricingElements: true,
                        IsActive: true
                    }),
                    RoomTypes.GetRoomTypes({ HotelId: HotelId, RateIds: [RateId] }),
                    Rates.GetRatePricingElements({
                        RateId: RateId,
                        IncludeDummies: true
                    }),
                    RateAssignment.GetRateAssignments({
                        EntityTypeId: EntityType.Customer,
                        RateId: RateId,
                        SkipCache: false,
                        includes: ['RateCustomerCommissions']
                    })
                ]);

                const assignedCustomers = Array.from(
                    new Map(rateAssignments.map(ra => [ra.EntityId, {
                        value: ra.EntityId,
                        text: ra.CustomerName,
                    }])).values()
                );

                setCustomers(assignedCustomers);

                const getSelectedPackages = () => {
                    var s = [...new Set(ratePricingElements
                        ?.flatMap(rpe => rpe.RatePricingElementsPackages
                            ?.map(rpep => rpep.PackageId)))];
                    return s;
                }

                let groupedPackages = groupByIntoArray(
                    ratePricingElements?.filter(rpe =>
                        rpe.EntityTypeId === EntityTypes.Package &&
                        rpe.IsActive && !rpe.IsOverride &&
                        getSelectedPackages().includes(rpe.EntityId)), "EntityId")
                    .filter(group => {
                        let deletedDerivedRPEs = group.filter((rpe) => ratePricingElements.find(x => rpe.EntityTypeId === x.EntityTypeId &&
                            rpe.EntityId === x.EntityId && x.IsOverride));
                        let activeRPEs = group.filter((rpe) => !rpe.IsOverride && !deletedDerivedRPEs.map(x => x.Id).includes(rpe.Id));
                        return activeRPEs.length > 0;
                    })
                    .map(group => {
                        return ({ value: group[0].EntityId, text: packages.find(rt => rt.Id === group[0].EntityId)?.Name });
                    });

                if (state.AllowSellRoomOnly) {
                    groupedPackages = [{
                        value: roomOnlyPackageKey,
                        text: 'Room Only'
                    }, ...groupedPackages];
                }

                setPackages(groupedPackages);

                const groupedRoomTypes = groupByIntoArray(ratePricingElements?.filter(rpe =>
                    rpe.EntityTypeId === EntityTypes.RoomType && rpe.IsActive && !rpe.IsOverride), "EntityId")
                    .filter(group => {
                        let deletedDerivedRPEs = group.filter((rpe) => ratePricingElements.find(x => rpe.EntityTypeId === x.EntityTypeId && rpe.EntityId === x.EntityId && x.IsOverride));
                        let activeRPEs = group.filter((rpe) => !rpe.IsOverride && !deletedDerivedRPEs.map(x => x.Id).includes(rpe.Id));
                        return activeRPEs.length > 0;
                    })
                    .map(group => {
                        const roomType = roomTypes.find(rt => rt.Id === group[0].EntityId);
                        return ({
                            Id: group[0].EntityId, 
                            Name: roomType?.Name,
                            ShortName: roomType?.ShortName
                        });
                    });

                setRoomTypes(groupedRoomTypes);

                loader(false);

            } catch (error) {
                onError(error);
                loader(false);
            }

        })();
    }, [RateId, HotelId]);

    const currencyFormatter = ({ value, row }) => {
        return formatCurrency(value || 0, currencyFormat(row?.CurrencyId));
    };

    const dateFormatter = ({ value }) => {
        return value && moment(value).format(dateFormat);
    };

    const isDayUse = () => {
        return datesRange && moment(datesRange[0]).format(dateFormat) == moment(datesRange[1]).format(dateFormat);
    };

    // Open restrictions popup with price list settings
    const openRestrictionsPopup = () => {
        if (window['FinalPriceRestrictionsController']) {
            window['FinalPriceRestrictionsController']('open', {
                HotelId: HotelId,
                RateID: RateId,
                DisableHotelLevel: true,
                LockHotel: true,
                LockRate: true,
                FilterRoomTypeIds: roomTypes.map(rt => rt.Id),
                FilterPackageIds: packages
                    .filter(p => p.value !== roomOnlyPackageKey)
                    .map(p => p.value),
                FilterCustomerIds: customers.map(c => c.value), 
                OnSave: () => {
                    if (doSearch.current) {
                        doSearch.current();
                    }
                }
            });
        }
    };

    // Base columns that are always shown
    const baseColumns = [
        { 
            name: 'ProductId', 
            title: text('Name'), 
            getCellValue: (row) => data.find(r => r.ProductId === row.ProductId)?.ProductName || ''
        },
        { name: "Date", title: text("Date"), formatter: dateFormatter },
        { name: "Description", title: text("Description") },
        { name: 'PriceBeforeTax', title: text('PriceBeforeTax'), formatter: currencyFormatter },
        { name: 'PriceAfterTax', title: text('PriceAfterTax'), formatter: currencyFormatter },
    ];

    // Restriction columns that are shown only when showRestrictions is true
    const restrictionColumns = showRestrictions ? [
        { 
            name: 'RestrictionType', 
            title: text('RestrictionType')
        },
        { 
            name: 'RestrictionMinimum', 
            title: text('Min')
        },
        { 
            name: 'RestrictionMaximum', 
            title: text('Max')
        },
    ] : [];

    const gridOptions = {
        search: {
            fields: [
                <DatePicker
                    picktype='range'
                    label={text('Dates')}
                    key='FromToDates'
                    onChange={setDatesRange}
                    required
                    clearable
                />,
                <Form.Field
                    label={text("RoomType")}
                    placeholder={text("Select")}
                    control={RoomTypesDropDown}
                    search
                    key='RoomTypeId'
                    data={roomTypes}
                    clearable
                />,
                <Form.Field
                    label={text("Packages")}
                    placeholder={text("Select")}
                    control={
                        ({ ...rest }) => <DropDown
                            data={packages}
                            textField="text"
                            valueField="value"
                            selectFirst={state.AllowSellRoomOnly}
                            {...rest}
                        />
                    }
                    search
                    spec={{ IsActive: true }}
                    onChange={setSelectedPackageId}
                    key='PackageId'
                    clearable
                />,
                <Form.Field
                    label={text("Customer")}
                    placeholder={text("Select")}
                    search
                    required
                    control={
                        ({ ...rest }) => <DropDown
                            data={customers}
                            textField="text"
                            valueField="value"
                            {...rest}
                        />
                    }
                    spec={{ HotelId: HotelId }}
                    key='CustomerId'
                    clearable
                />,
                <Form.Field
                    label={text("Pax")}
                    control={PaxInput}
                    required
                    key='Pax'
                />,
                <Form.Field
                    control={CheckboxWrapper}
                    label={text("ShowRestrictions")}
                    onChange={setShowRestrictions}
                    value={showRestrictions}
                    key='ShowRestrictions'
                />,
            ],
            method: 'GET',
            api: Products.SearchWithoutLimits,
            setDoSearchRef: (ref) => (doSearch.current = ref),
            spec: {
                includes: [
                    "Rate",
                    "ProductPrices",
                    "DailyProductPrices",
                    "DailyProductPricesLogs",
                    "Restrictions",
                    "RatePrices",
                    "Commissions"
                ],
                GetPricingForLastDateOfRange: isDayUse(),
                HotelIds: [HotelId],
                RateId: RateId,
                RoomOnlyProducts: selectedPackageId === roomOnlyPackageKey,
                PackageId: selectedPackageId === roomOnlyPackageKey ? null : selectedPackageId,
                ConsiderCustomerDiscount: true,
                IgnoreDayUse: true,
                IgnoreLRV: true
            },
            validate: (state) => {
                const isPaxSet = Boolean(state["Pax.Adults"] || state["Pax.Children"] || state["Pax.Infants"]);
                if (!isPaxSet) {
                    onWarning(message('MaxPaxIsRequired'));
                }

                return isPaxSet;
            },
        },
        grid: {
            columns: [...baseColumns, ...restrictionColumns],
            showFilter: true,
            filtering: true,
            customOptions: {
                group: {
                    grouping: [
                        { columnName: 'ProductId' },
                        { columnName: 'Date' }
                    ]
                }
            },
            summary: true,
            groupSummaryItems: [
                { columnName: 'PriceBeforeTax', type: 'sum', showInGroupFooter: false, alignByColumn: true, formatter: currencyFormatter },
                { columnName: 'PriceAfterTax', type: 'sum', showInGroupFooter: false, alignByColumn: true, formatter: currencyFormatter }
            ],
            menu: [
                <Popup
                    key="restrictions-icon"
                    position="bottom center"
                    content={text('RestrictionPopup')}
                    trigger={
                        <Icon 
                            size="big" 
                            className="icon-do-not-disturbe" 
                            onClick={openRestrictionsPopup}
                            style={{ cursor: 'pointer', verticalAlign: 'middle' }}
                        />
                    }
                />
            ]
        }
    };

    return (
        <div>
        <GridSearchTemplate 
            options={gridOptions} 
            data={data} 
            onSearchComplete={setData} 
        />

        <RestrictionsPopup
            HotelID={HotelId}
            controller="FinalPriceRestrictionsController"
            startOpen={false}
        />
        </div>
    );
}

FinalPrice.propTypes = {
    HotelId: PropTypes.number,
    RateId: PropTypes.number
};
